/*
 * DFU error codes and messages. Port of pc-nrf-dfu-js DfuError.js, with the
 * codes that are relevant to USB serial DFU. BLE and Sink transports omitted.
 */

export const ErrorCode = {
  // Error message types (top byte)
  ERROR_MESSAGE: 0x00,
  ERROR_MESSAGE_RSP: 0x01,
  ERROR_MESSAGE_EXT: 0x02,

  // Library-level errors
  ERROR_PRE_DFU_INTERRUPTED: 0x0001,
  ERROR_UNEXPECTED_BYTES: 0x0002,
  ERROR_CRC_MISMATCH: 0x0003,
  ERROR_TOO_MANY_WRITE_FAILURES: 0x0004,
  ERROR_CAN_NOT_USE_HIGHER_PRN: 0x0011,
  ERROR_READ_CONFLICT: 0x0012,
  ERROR_TIMEOUT_READING_SERIAL: 0x0013,
  ERROR_RECEIVE_TWO_MESSAGES: 0x0014,
  ERROR_RESPONSE_NOT_START_WITH_0x60: 0x0015,
  ERROR_ASSERT_EMPTY_RESPONSE: 0x0016,
  ERROR_UNEXPECTED_RESPONSE_OPCODE: 0x0017,
  ERROR_UNEXPECTED_RESPONSE_BYTES: 0x0018,
  ERROR_UNABLE_FIND_PORT: 0x0072,
  ERROR_PORT_NOT_OPEN: 0x0073,
  ERROR_DEVICE_DISCONNECTED: 0x0074,

  // Response error codes from the target (top byte 0x01)
  ERROR_RSP_INVALID: 0x0100,
  ERROR_RSP_SUCCESS: 0x0101,
  ERROR_RSP_OP_CODE_NOT_SUPPORTED: 0x0102,
  ERROR_RSP_INVALID_PARAMETER: 0x0103,
  ERROR_RSP_INSUFFICIENT_RESOURCES: 0x0104,
  ERROR_RSP_INVALID_OBJECT: 0x0105,
  ERROR_RSP_UNSUPPORTED_TYPE: 0x0107,
  ERROR_RSP_OPERATION_NOT_PERMITTED: 0x0108,
  ERROR_RSP_OPERATION_FAILED: 0x010a,
  ERROR_RSP_EXT_ERROR: 0x010b,

  // Extended error codes (top byte 0x02)
  ERROR_EXT_NO_ERROR: 0x0200,
  ERROR_EXT_INVALID_ERROR_CODE: 0x0201,
  ERROR_EXT_WRONG_COMMAND_FORMAT: 0x0202,
  ERROR_EXT_UNKNOWN_COMMAND: 0x0203,
  ERROR_EXT_INIT_COMMAND_INVALID: 0x0204,
  ERROR_EXT_FW_VERSION_FAILURE: 0x0205,
  ERROR_EXT_HW_VERSION_FAILURE: 0x0206,
  ERROR_EXT_SD_VERSION_FAILURE: 0x0207,
  ERROR_EXT_SIGNATURE_MISSING: 0x0208,
  ERROR_EXT_WRONG_HASH_TYPE: 0x0209,
  ERROR_EXT_HASH_FAILED: 0x020a,
  ERROR_EXT_WRONG_SIGNATURE_TYPE: 0x020b,
  ERROR_EXT_VERIFICATION_FAILED: 0x020c,
  ERROR_EXT_INSUFFICIENT_SPACE: 0x020d,
  ERROR_EXT_FW_ALREADY_PRESENT: 0x020e,
} as const;

export type ErrorCodeValue = (typeof ErrorCode)[keyof typeof ErrorCode];

const ErrorMessages: Record<number, string> = {
  [ErrorCode.ERROR_PRE_DFU_INTERRUPTED]:
    "A previous DFU was interrupted and cannot be continued. Restart and retry.",
  [ErrorCode.ERROR_UNEXPECTED_BYTES]: "Unexpected byte count in transfer.",
  [ErrorCode.ERROR_CRC_MISMATCH]: "CRC mismatch.",
  [ErrorCode.ERROR_TOO_MANY_WRITE_FAILURES]: "Too many write failures.",
  [ErrorCode.ERROR_CAN_NOT_USE_HIGHER_PRN]: "DFU PRN must fit in 16 bits.",
  [ErrorCode.ERROR_READ_CONFLICT]: "Concurrent reads from the DFU transport are not allowed.",
  [ErrorCode.ERROR_TIMEOUT_READING_SERIAL]: "Timed out waiting for a serial response from the bootloader.",
  [ErrorCode.ERROR_RECEIVE_TWO_MESSAGES]: "DFU transport received two messages at once.",
  [ErrorCode.ERROR_RESPONSE_NOT_START_WITH_0x60]: "Response from DFU target did not start with 0x60.",
  [ErrorCode.ERROR_ASSERT_EMPTY_RESPONSE]: "Empty response from DFU target.",
  [ErrorCode.ERROR_UNEXPECTED_RESPONSE_OPCODE]: "Unexpected opcode in DFU response.",
  [ErrorCode.ERROR_UNEXPECTED_RESPONSE_BYTES]: "Unexpected payload length in DFU response.",
  [ErrorCode.ERROR_UNABLE_FIND_PORT]: "Unable to find serial port.",
  [ErrorCode.ERROR_PORT_NOT_OPEN]: "Serial port is not open.",
  [ErrorCode.ERROR_DEVICE_DISCONNECTED]: "Device disconnected unexpectedly.",
};

const ResponseErrorMessages: Record<number, string> = {
  [ErrorCode.ERROR_RSP_INVALID]: "Missing or malformed opcode.",
  [ErrorCode.ERROR_RSP_OP_CODE_NOT_SUPPORTED]: "Opcode unknown or not supported.",
  [ErrorCode.ERROR_RSP_INVALID_PARAMETER]: "A parameter for the opcode was missing.",
  [ErrorCode.ERROR_RSP_INSUFFICIENT_RESOURCES]: "Not enough memory for the data object.",
  [ErrorCode.ERROR_RSP_INVALID_OBJECT]:
    "The data object didn't match firmware/hardware, or its signature/CRC failed, or it could not be parsed.",
  [ErrorCode.ERROR_RSP_UNSUPPORTED_TYPE]: "Unsupported object type.",
  [ErrorCode.ERROR_RSP_OPERATION_NOT_PERMITTED]: "Operation not permitted in current DFU state.",
  [ErrorCode.ERROR_RSP_OPERATION_FAILED]: "Operation failed.",
};

const ExtendedErrorMessages: Record<number, string> = {
  [ErrorCode.ERROR_EXT_NO_ERROR]: "An error happened, but its extended code is unset.",
  [ErrorCode.ERROR_EXT_INVALID_ERROR_CODE]: "An error happened, but its extended code is invalid.",
  [ErrorCode.ERROR_EXT_WRONG_COMMAND_FORMAT]: "Command format incorrect.",
  [ErrorCode.ERROR_EXT_UNKNOWN_COMMAND]: "Command unknown or unsupported by this bootloader.",
  [ErrorCode.ERROR_EXT_INIT_COMMAND_INVALID]: "Init packet is invalid for this update type.",
  [ErrorCode.ERROR_EXT_FW_VERSION_FAILURE]: "Firmware version too low (downgrade prevented).",
  [ErrorCode.ERROR_EXT_HW_VERSION_FAILURE]: "Hardware version mismatch.",
  [ErrorCode.ERROR_EXT_SD_VERSION_FAILURE]: "SoftDevice version mismatch.",
  [ErrorCode.ERROR_EXT_SIGNATURE_MISSING]: "Bootloader requires a signed init packet.",
  [ErrorCode.ERROR_EXT_WRONG_HASH_TYPE]: "Hash type not supported.",
  [ErrorCode.ERROR_EXT_HASH_FAILED]: "Hash computation failed.",
  [ErrorCode.ERROR_EXT_WRONG_SIGNATURE_TYPE]: "Signature type not supported.",
  [ErrorCode.ERROR_EXT_VERIFICATION_FAILED]: "Firmware image hash mismatch.",
  [ErrorCode.ERROR_EXT_INSUFFICIENT_SPACE]: "Not enough flash space for the firmware.",
  [ErrorCode.ERROR_EXT_FW_ALREADY_PRESENT]: "Firmware already present on device.",
};

export class DfuError extends Error {
  readonly code: number;
  constructor(code: number, extra?: string) {
    const type = code >> 8;
    let base: string;
    if (type === 0x00) base = ErrorMessages[code] ?? `Unknown DFU error 0x${code.toString(16)}`;
    else if (type === 0x01)
      base = `DFU target rejected request: ${
        ResponseErrorMessages[code] ?? `code 0x${(code & 0xff).toString(16)}`
      }`;
    else if (type === 0x02)
      base = `DFU target extended error: ${
        ExtendedErrorMessages[code] ?? `code 0x${(code & 0xff).toString(16)}`
      }`;
    else base = `Unknown DFU error type 0x${type.toString(16)}`;
    super(extra ? `${base} ${extra}` : base);
    this.name = "DfuError";
    this.code = code;
  }
}
